import { useEffect, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { APPLICANT_ATTRIBUTIONS, CUSTOMER_DATA_TABS, LEAD_DETAILS_FORMS } from 'cpool-constants';
import type { Schema_Wohnsituation } from 'cpool-schemas';
import { Anschrift } from 'cpool-schemas';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Address from './Address.tsx';
import Children from './Children.tsx';
import Haushalt from './Haushalt.tsx';
import PreviousAddress from './PreviousAddress.tsx';
import { hide } from '~/features/leadDetails/components/Overview/CustomerData/General/utils/hide.ts';
import { ApplicantCollapsible } from '~/features/leadDetails/components/Overview/CustomerData/shared/ApplicantCollapsible.tsx';
import { useCustomerDataTabsContext } from '~/features/leadDetails/context/CustomerDataTabsContext.tsx';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { extractErrorPaths, useTabErrorNotification } from '~/features/leadDetails/hooks/useTabErrorNotification.ts';
import {
    schemaLivingSituationForm,
    type SchemaLivingSituationForm,
} from '~/features/leadDetails/schemas/living.schema.ts';
import { generateLivingSituationCommands } from '~/features/leadDetails/utils/generateLivingSituationCommands.ts';
import { submitCommands } from '~/features/leadDetails/utils/submitCommands.ts';
import { useOnMountAndChange } from '~/hooks/useOnMountAndChange.tsx';

const formId = LEAD_DETAILS_FORMS.LIVING_SITUATION;

// Ensure address defaults exist so the form is not dirty after its initial render.
function prepareWohnsituation(livingSituation: Schema_Wohnsituation): Schema_Wohnsituation {
    const { anschrift, vorherigeAnschrift, ...rest } = livingSituation;
    return {
        ...rest,
        anschrift: anschrift || Anschrift.parse({}),
        vorherigeAnschrift: vorherigeAnschrift || Anschrift.parse({}),
    };
}

const LivingSituation: React.FC<{
    lead: SchemaLoanRequestResolver_Get & {
        antragsteller1: NonNullable<SchemaLoanRequestResolver_Get['antragsteller1']>;
    };
}> = ({ lead }) => {
    const { t } = useTranslation();
    const { notifications, tab } = useCustomerDataTabsContext();
    const { registerDirtyState, registerReset, registerSubmit } = useLeadDetailsFormContext();

    const methods = useForm<SchemaLivingSituationForm>({
        defaultValues: {
            antragsteller1: {
                familienstand: lead.antragsteller1.personendaten.familienstand,
                wohnsituation: prepareWohnsituation(lead.antragsteller1.wohnsituation),
            },
            antragsteller2: {
                familienstand: lead.antragsteller2?.personendaten.familienstand,
                wohnsituation: lead.antragsteller2
                    ? prepareWohnsituation(lead.antragsteller2.wohnsituation)
                    : undefined,
            },
            kinder: lead.haushalt.kinder ?? [],
        },
        mode: 'onChange',
        resolver: zodResolver(schemaLivingSituationForm),
    });

    // NOTE: we need to set the default values for the antragsteller2 field when the second applicant is added to the form
    const hadAntragsteller2 = useRef(!!lead.antragsteller2);
    useEffect(() => {
        if (!hadAntragsteller2.current && lead.antragsteller2) {
            hadAntragsteller2.current = true;
            methods.reset(
                {
                    antragsteller1: {
                        familienstand: lead.antragsteller1.personendaten.familienstand,
                        wohnsituation: prepareWohnsituation(lead.antragsteller1.wohnsituation),
                    },
                    antragsteller2: {
                        familienstand: lead.antragsteller2.personendaten.familienstand,
                        wohnsituation: prepareWohnsituation(lead.antragsteller2.wohnsituation),
                    },
                    kinder: lead.haushalt.kinder ?? [],
                },
                { keepDirty: false, keepErrors: false, keepTouched: false },
            );
        }
    }, [lead, lead.antragsteller2, methods]);

    useTabErrorNotification(CUSTOMER_DATA_TABS.LIVING_SITUATION, methods.formState.errors);

    useEffect(() => {
        registerDirtyState(formId, {
            dirty: methods.formState.isDirty,
            dirtyFields: methods.formState.dirtyFields,
        });
    }, [registerDirtyState, methods.formState, methods.formState.isDirty]);

    registerReset(formId, methods.reset);

    useOnMountAndChange(() => {
        registerSubmit(
            (setSubmitStatus) =>
                methods.handleSubmit(
                    async (data) => {
                        const receivedLeadData = lead.antragsteller2
                            ? {
                                  antragsteller1: {
                                      familienstand: lead.antragsteller1.personendaten.familienstand,
                                      wohnsituation: lead.antragsteller1.wohnsituation ?? {},
                                  },
                                  antragsteller2: {
                                      familienstand: lead.antragsteller2.personendaten.familienstand,
                                      wohnsituation: lead.antragsteller2.wohnsituation,
                                  },
                                  kinder: lead.haushalt.kinder,
                              }
                            : {
                                  antragsteller1: {
                                      familienstand: lead.antragsteller1.personendaten.familienstand,
                                      wohnsituation: lead.antragsteller1.wohnsituation,
                                  },
                                  kinder: lead.haushalt.kinder,
                              };

                        const commands = generateLivingSituationCommands({
                            applicantId1: lead.antragsteller1.id,
                            applicantId2: lead.antragsteller2?.id,
                            newData: data,
                            oldData: receivedLeadData,
                        });

                        await submitCommands(lead.id, setSubmitStatus, commands, methods.reset);
                    },
                    (error) => {
                        setSubmitStatus({ changes: true, error, saved: false });
                    },
                ),
            formId,
            methods.trigger,
        );
    }, [lead]);

    const hasSecondApplicant = !!lead.antragsteller2;
    const getApplicantErrorCount = (applicant: 1 | 2) => {
        const attribution =
            applicant === 1 ? APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1 : APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2;
        const formErrors = methods.formState.errors[`antragsteller${applicant}`] ?? {};
        const businessRuleErrors = notifications[CUSTOMER_DATA_TABS.LIVING_SITUATION].filter((notification) =>
            notification.startsWith(`${attribution}-`),
        );

        return extractErrorPaths(formErrors).length + businessRuleErrors.length;
    };

    // NOTE: Address renders nothing for the applicant without an address of their own,
    // i.e. for applicant 2 while both applicants share a household
    const livingSituationElements = (antragsteller: 1 | 2) => {
        return (
            <div className="space-y-2">
                <Address antragsteller={antragsteller} />
                <PreviousAddress antragsteller={antragsteller} />
                <Haushalt antragsteller={antragsteller} />
            </div>
        );
    };

    return (
        <FormProvider {...methods}>
            <form id={formId} onSubmit={(e) => e.preventDefault()}>
                <div className="mb-2" style={hide(tab !== CUSTOMER_DATA_TABS.LIVING_SITUATION)}>
                    {hasSecondApplicant ? (
                        <div className="space-y-2">
                            <ApplicantCollapsible
                                applicant={1}
                                collapsible
                                errorCount={getApplicantErrorCount(1)}
                                testId="living-situation-applicant1"
                            >
                                <section className="@container" data-testid="applicant1">
                                    {livingSituationElements(1)}
                                </section>
                            </ApplicantCollapsible>
                            <ApplicantCollapsible
                                applicant={2}
                                collapsible
                                errorCount={getApplicantErrorCount(2)}
                                style={hide(lead.coApplicantHidden)}
                                testId="living-situation-applicant2"
                            >
                                <section className="@container" data-testid="applicant2">
                                    {livingSituationElements(2)}
                                </section>
                            </ApplicantCollapsible>
                        </div>
                    ) : (
                        <section className="@container space-y-2">{livingSituationElements(1)}</section>
                    )}
                    <section className="@container mt-2">
                        <Children
                            hasSecondApplicant={hasSecondApplicant}
                            secondApplicantHidden={hasSecondApplicant && lead.coApplicantHidden}
                        />
                    </section>
                    {lead.coApplicantHidden && (
                        <div className="flex justify-center my-6">
                            <span className="text-gray-400">{t('OVERVIEW.SECOND_APPLICANT_HIDDEN')}</span>
                        </div>
                    )}
                </div>
            </form>
        </FormProvider>
    );
};

export default LivingSituation;
